import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { forwardRef, ReactNode, useImperativeHandle, useMemo } from 'react';

import { useUserContentDismissal } from '../hooks';

interface UserContentDismissalRenderProps {
	name: string;
	namespace: string;
	children: (updateUserContentDismissal: VoidFunction) => ReactNode;
}

export interface UserContentDismissalRef {
	dismiss: () => void;
}

const UserContentDismissalComponent = forwardRef<UserContentDismissalRef, UserContentDismissalRenderProps>(
	({ name, namespace, children }: UserContentDismissalRenderProps, ref) => {
		useImperativeHandle(ref, () => ({
			dismiss: () => {
				updateUserContentDismissal();
			},
		}));
		const { loading, data, error, updateUserContentDismissal } = useUserContentDismissal({
			name,
			namespace,
		});

		if (loading) {
			return null;
		}

		if (error || !data || !data.dismissed_at) {
			return <>{children(updateUserContentDismissal)}</>;
		} else {
			return null;
		}
	}
);

UserContentDismissalComponent.displayName = 'UserContentDismissalComponent';

/**
 * In order for the `useUserContentDismissal` hook to use `useQueryClient`
 * this component is wrapped in a `QueryClientProvider` to ensure an instance is
 * always available
 *
 * It also reduces the amount of dependencies required in order to import
 * and use this component
 */
export const UserContentDismissal = forwardRef<UserContentDismissalRef, UserContentDismissalRenderProps>((props, ref) => {
	const queryClient = useMemo(() => new QueryClient(), []);

	return (
		<QueryClientProvider client={queryClient}>
			<UserContentDismissalComponent {...props} ref={ref} />
		</QueryClientProvider>
	);
});

UserContentDismissal.displayName = 'UserContentDismissal';
